// The Scheduled panel (unattended.md section 11): what Quanty One will come back for, with Cancel and
// Resume per entry and the Pause all switch. Read when opened and every 30 seconds while open.
import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { abandonQuestion, describeEntry, type Agenda } from "../agenda.js";
import { get, post } from "../api.js";
import type { ConfirmRequest } from "./Confirm.js";
import { Button } from "./ui/button.js";

export function AgendaPanel({ onSignedOut, onClose, confirm }: { onSignedOut(): void; onClose(): void; confirm(r: ConfirmRequest): void }) {
  const [agenda, setAgenda] = useState<Agenda>();
  const [notice, setNotice] = useState<string>();

  const load = useCallback(async () => {
    try {
      const res = await get<Agenda>("/api/agenda");
      if (res.status === 401) return onSignedOut();
      if (res.status === 200) return setAgenda(res.data);
    } catch {
      // Fall through.
    }
    setNotice("The schedule could not be loaded.");
  }, [onSignedOut]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  const act = async (path: string, body?: unknown): Promise<void> => {
    setNotice(undefined);
    try {
      const res = await post<{ error?: string; handles?: string[]; more?: boolean; generation?: number }>(path, body);
      if (res.status === 401) return onSignedOut();
      if (res.status === 409 && res.data.error === "runs_unfinished") {
        const generation = res.data.generation;
        confirm({
          title: "Stop following these runs?",
          body: abandonQuestion(res.data.handles ?? [], res.data.more === true),
          confirm: "Stop following",
          onConfirm: () => void act(path, { abandon: true, generation }),
        });
        return;
      }
      if (res.status !== 200) setNotice("That did not go through. Try again.");
    } catch {
      setNotice("That did not go through. Try again.");
    }
    await load();
  };

  return (
    <aside className="flex h-full w-full max-w-sm shrink-0 flex-col border-l border-border bg-card" aria-label="Scheduled">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-border px-3">
        <span className="flex-1 text-sm font-semibold">Scheduled</span>
        {agenda && (
          <Button variant="outline" size="sm" onClick={() => void act(agenda.paused ? "/api/agenda/resume" : "/api/agenda/pause")}>
            {agenda.paused ? "Resume all" : "Pause all"}
          </Button>
        )}
        <Button variant="ghost" size="icon-sm" aria-label="Close Scheduled" onClick={onClose}>
          <X />
        </Button>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {notice && <p className="rounded-md bg-error-bg px-3 py-2 text-sm text-error">{notice}</p>}
        {!agenda && !notice && <p className="text-sm text-muted-foreground">Loading…</p>}
        {agenda?.entries.length === 0 && <p className="text-sm text-muted-foreground">Nothing is scheduled. Ask Quanty One to check back later, or to repeat a task.</p>}
        {agenda?.entries.map((e) => (
          <div key={e.id} className="rounded-lg border border-border bg-background p-3 text-sm">
            <div className="font-medium">{e.what}</div>
            <div className="mt-1 text-xs text-muted-foreground">{describeEntry(e)}</div>
            <div className="mt-2 flex gap-2">
              {e.state === "paused" && (
                <Button variant="outline" size="sm" onClick={() => void act(`/api/agenda/${e.id}/resume`)}>
                  Resume
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => void act(`/api/agenda/${e.id}/cancel`)}>
                Cancel
              </Button>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}
